import { AdminMessage, FileMessage, UserMessage } from "sendbird";
import format from "date-fns/format";

export type CoreMessageType = AdminMessage | UserMessage | FileMessage;
export type MessageType = {
  _id: string;
  text: string;
  createdAt: number | Date;
  user: {
    _id: string;
    name: string;
    avatar: string;
  };
  system?: boolean;
  image?: string;
  video?: string;
  parent?: {
    _id: string | number;
    text: string;
    name?: string;
  };
};

export const isAdminMessage = (message: AdminMessage): boolean =>
  message &&
  (message.isAdminMessage?.() ||
    (message["messageType"] && message.messageType === "admin"));
export const isUserMessage = (message: UserMessage): boolean =>
  message &&
  (message.isUserMessage?.() ||
    (message["messageType"] && message.messageType === "user"));
export const isFileMessage = (message: FileMessage): boolean =>
  message &&
  (message.isFileMessage?.() ||
    (message["messageType"] && message.messageType === "file"));

export const isOGMessage = (message: UserMessage): boolean =>
  !!(
    message &&
    isUserMessage(message) &&
    message?.ogMetaData &&
    message?.ogMetaData?.url
  );

export const isSupportedFileView = (type: string): boolean =>
  isImage(type) || isVideo(type);
export const isTextMessage = (message: UserMessage): boolean =>
  isUserMessage(message) && !isOGMessage(message);
export const isThumbnailMessage = (message: FileMessage): boolean =>
  message && isFileMessage(message) && isSupportedFileView(message.type);
export const isImageMessage = (message: FileMessage): boolean =>
  message && isThumbnailMessage(message) && isImage(message.type);
export const isVideoMessage = (message: FileMessage): boolean =>
  message && isThumbnailMessage(message) && isVideo(message.type);

export const isJsonString = (str = "") => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};
export const isImage = (type) => {
  return type.match(/^image\/.+$/);
};

export const isVideo = (type) => {
  return type.match(/^video\/.+$/);
};

/**
 * SendBird > Formatter
 * @param {Object} messages SendBird messages object
 * @param {string} messageId Message ID
 * @param {Object} _sender Sender object
 * @param {string} message Message text
 * @param {string} createdAt Date of message set as moment()
 * @returns {Object} Message formatted for GiftedChat
 */
export const formatMessage = (messageObj) => {
  const {
    messageId,
    message,
    url,
    createdAt,
    _sender,
    type,
    data,
    messageType,
  } = messageObj;

  let parent = null;
  if (data && isJsonString(data)) {
    const parsedData = JSON.parse(data);
    if (parsedData.parentMessageId) {
      parent = {
        _id: parsedData.parentMessageId,
        text: parsedData.parentMessageText,
        name: parsedData?._sender?.nickname,
      };
    }
  }

  const formattedMessage = {
    _id: messageId?.toString(),
    text: messageType === "file" ? "" : message,
    createdAt: createdAt,
    image: "",
    video: "",
    user: {
      _id: _sender?.userId,
      name: _sender?.nickname,
      avatar: _sender?.plainProfileUrl,
    },
    system: false,
    parent: parent,
  };

  if (messageObj.isFileMessage()) {
    if (isImage(type)) {
      formattedMessage.image = url;
    }

    if (isVideo(type)) {
      formattedMessage.video = url;
    }
  }

  if (messageObj.isAdminMessage()) {
    formattedMessage.system = true;
  }

  return formattedMessage;
};

export const isSameUser = (currentMessage, diffMessage) => {
  return !!(
    diffMessage &&
    diffMessage.user &&
    currentMessage.user &&
    diffMessage.user._id === currentMessage.user._id
  );
};

export const getParentObj = (parent) => {
  let parentMessageText = "";

  if (parent?.image) {
    parentMessageText = "photo";
  } else if (parent?.video) {
    parentMessageText = "video";
  } else if (parent?.text) {
    parentMessageText = parent?.text;
  } else {
    parentMessageText = "";
  }

  const parentObj = {
    parentMessageId: parent?._id,
    parentMessageText,
    _sender: {
      nickname: parent?.user?.name,
    },
  };
  return parentObj;
};

export const getMentionedUsers = (message) => {
  const mentionedUserIds = message
    ?.trim()
    ?.split("@")
    ?.flatMap((item) => {
      const matcher = item.match(/^\[([^\[]*?)\]\(([^\(]*?)\)/g);

      if (matcher && matcher[0]) {
        const mentionedUserId = matcher[0].substring(
          matcher[0].indexOf("(") + 1,
          matcher[0].indexOf(")")
        );

        return mentionedUserId;
      }

      return [];
    });

  return mentionedUserIds;
};

export const getMessageCreatedAt = (
  message: UserMessage | FileMessage
): string => format(message.createdAt, "p");

export const isSameGroup = (
  message: AdminMessage | UserMessage | FileMessage,
  comparingMessage: AdminMessage | UserMessage | FileMessage
): boolean => {
  if (
    !(
      message &&
      comparingMessage &&
      message?.messageType &&
      message.messageType !== "admin" &&
      comparingMessage?.messageType &&
      comparingMessage.messageType !== "admin" &&
      (message as UserMessage | FileMessage)?.sender &&
      (comparingMessage as UserMessage | FileMessage)?.sender &&
      message?.createdAt &&
      comparingMessage?.createdAt &&
      (message as UserMessage | FileMessage)?.sender?.userId &&
      (comparingMessage as UserMessage | FileMessage)?.sender?.userId
    )
  ) {
    return false;
  }
  // to fix typecasting
  const message_ = message as UserMessage;
  const comparingMessage_ = comparingMessage as UserMessage;
  return (
    message_?.sendingStatus === comparingMessage_?.sendingStatus &&
    message_?.sender?.userId === comparingMessage_?.sender?.userId &&
    getMessageCreatedAt(message as UserMessage | FileMessage) ===
      getMessageCreatedAt(comparingMessage as UserMessage | FileMessage)
  );
};

export const compareMessagesForGrouping = (
  prevMessage: AdminMessage | UserMessage | FileMessage,
  currMessage: AdminMessage | UserMessage | FileMessage,
  nextMessage: AdminMessage | UserMessage | FileMessage
): [boolean, boolean] => [
  isSameGroup(prevMessage, currMessage),
  isSameGroup(currMessage, nextMessage),
];
